import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Portal } from "@mui/material";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useTranslation } from "common/locales";
import { useState } from "react";
import AddNewEventMenu from "./AddNewEventMenu";
import { useAddNewEventDialogs } from "./useAddNewEventDialogs";

interface AddNewEventButtonProps {
  maxWidth?: string;
}

export default function AddNewEventButton({
  maxWidth = "150px",
}: AddNewEventButtonProps) {
  const [eventMenuAnchor, setEventMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [buttonPosition, setButtonPosition] = useState<DOMRect | null>(null);
  const eventMenuOpen = Boolean(eventMenuAnchor);
  const { handleEventTypeClick, dialogs } = useAddNewEventDialogs();
  const { t } = useTranslation();

  const handleEventMenuOpen = (event: React.MouseEvent<HTMLElement> | void) => {
    if (event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setButtonPosition(rect);
      setEventMenuAnchor(event.currentTarget ?? null);
    }
  };

  const handleEventMenuClose = () => {
    setEventMenuAnchor(null);
  };

  const handleEventMenuItemClick = (eventType: string) => {
    handleEventMenuClose();
    handleEventTypeClick(eventType);
  };

  return (
    <>
      <Button
        variant="contained"
        startIcon={
          <AddCircleIcon
            sx={{ color: "#ffffff !important", fill: "#ffffff !important" }}
          />
        }
        onClick={handleEventMenuOpen}
        sx={{
          height: "auto",
          width: maxWidth === "100%" ? "100%" : "auto",
          maxWidth: maxWidth,
          fontSize: "13px",
          fontWeight: 500,
          textTransform: "none",
          whiteSpace: "nowrap",
          backgroundColor: COLORS.dahlia[700],
          color: "#fafafa",
          borderRadius: `${BORDER_RADIUS.small}px`,
          padding: "10px 12px",
          "&:hover": {
            backgroundColor: COLORS.dahlia[900],
          },
        }}
      >
        {t("addNewEvent.button")}
      </Button>

      {/* Close button shown above backdrop when menu is open */}
      {eventMenuOpen && buttonPosition && (
        <Portal>
          <Box
            sx={{
              position: "fixed",
              top: buttonPosition.top,
              left: buttonPosition.left,
              width: buttonPosition.width,
              height: buttonPosition.height,
              zIndex: 1401,
              pointerEvents: "auto",
            }}
          >
            <Button
              variant="outlined"
              startIcon={
                <CloseIcon
                  sx={{
                    color: `${COLORS.bellflower[700]} !important`,
                    fontSize: "20px",
                  }}
                />
              }
              onClick={handleEventMenuClose}
              sx={{
                height: "100%",
                width: "100%",
                fontSize: "14px",
                fontWeight: 500,
                textTransform: "none",
                backgroundColor: COLORS.bellflower[100],
                color: COLORS.bellflower[900],
                borderColor: COLORS.bellflower[300],
                borderRadius: `${BORDER_RADIUS.small}px`,
                padding: "10px 12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                "&:hover": {
                  backgroundColor: COLORS.bellflower[300],
                  borderColor: COLORS.bellflower[500],
                },
              }}
            >
              Close
            </Button>
          </Box>
        </Portal>
      )}

      <AddNewEventMenu
        anchorEl={eventMenuAnchor}
        open={eventMenuOpen}
        onClose={handleEventMenuClose}
        onEventTypeClick={handleEventMenuItemClick}
        animated
      />

      {dialogs}
    </>
  );
}
